<template>
  <div class="contents">
    <TransitionGroup v-bind="stackListTransition">
      <FloatingActionBar v-for="test in draftSplitTests" :key="test.pid" data-test="unstarted-split-tests">
        <ExclamationCircleIcon class="size-[18px] shrink-0 text-amber-300" />
        <RouterLink
          :to="{ name: ROUTES.SPLIT_TESTS, params: { teamPid: currentTeamPid }, query: { testId: test.pid } }"
          class="min-w-0 cursor-pointer truncate underline decoration-white/40 underline-offset-4"
          >"{{ test.name }}" test not yet started - Review and start</RouterLink
        >
        <button
          type="button"
          class="ml-1 flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-lg text-white/70 hover:bg-white/10 hover:text-white"
          :aria-label="`Dismiss reminder for ${test.name}`"
          data-test="dismiss-unstarted-split-test"
          @click="dismissSplitTestReminder(test.pid)">
          <XMarkIcon class="size-4" />
        </button>
      </FloatingActionBar>
      <FloatingActionBar v-if="publishBarShown" key="publish-bar" ref="barRef" data-shell="publish-bar">
        <template v-if="isPaymentFailed && currentTeam && !isDemo && !isPublishingChanges">
          <CreditCardIcon class="size-[18px] shrink-0 text-red-300" />
          <span class="mr-[18px]">
            Payment failed. {{ configChanges.length }} {{ configChanges.length === 1 ? 'change is' : 'changes are' }}
            waiting.
          </span>
          <button
            type="button"
            class="h-[34px] shrink-0 cursor-pointer rounded-lg border border-red-300 px-3.5 text-[13px] font-semibold text-red-200"
            @click="goToBillingPortal"
            >Update payment to publish</button
          >
        </template>
        <template v-else>
          <button
            ref="changesTriggerRef"
            type="button"
            :aria-expanded="changesOpen"
            :aria-controls="changesPopoverId"
            :disabled="isPublishingChanges"
            class="mr-2.5 flex h-[34px] min-w-0 cursor-pointer items-center gap-2 rounded-lg pr-2.5 pl-1 disabled:cursor-not-allowed disabled:opacity-40"
            :class="changesOpen ? 'bg-white/10' : ''"
            @mouseenter="scheduleChangesOpen"
            @mouseleave="scheduleChangesClose"
            @click="toggleChangesPopover">
            <ExclamationCircleIcon class="size-[18px] shrink-0 text-amber-300" />
            <span class="truncate underline decoration-white/40 decoration-dotted underline-offset-4"
              >{{ configChanges.length }} unpublished {{ configChanges.length === 1 ? 'change' : 'changes' }}</span
            >
            <ChevronUpIcon class="size-3 shrink-0 opacity-60" />
          </button>
          <span
            v-tooltip.top="previewDisabled ? 'Install the tracking snippet to preview' : undefined"
            class="shrink-0"
            :class="previewDisabled || isPublishingChanges ? 'opacity-40' : ''">
            <div ref="previewMenuRef" class="relative">
              <div class="flex h-[34px] rounded-lg border border-white/[.14] bg-white/[.06]">
                <button
                  data-test="preview-primary-button"
                  type="button"
                  aria-label="Preview on your site"
                  :disabled="previewDisabled || isPublishingChanges"
                  class="flex cursor-pointer items-center gap-1.5 rounded-l-lg pr-2.5 pl-3 font-medium hover:bg-white/10 disabled:cursor-not-allowed"
                  @click="openActivePreview">
                  <EyeIcon class="size-4" /><span class="hidden whitespace-nowrap lg:inline">Preview on your site</span>
                </button>
                <button
                  data-test="preview-menu-button"
                  type="button"
                  aria-label="Choose preview URL"
                  :aria-expanded="previewMenuOpen"
                  :disabled="previewDisabled || isPublishingChanges"
                  class="flex cursor-pointer items-center rounded-r-lg border-l border-white/[.14] px-2 hover:bg-white/10 disabled:cursor-not-allowed"
                  @click="togglePreviewMenu"
                  ><ChevronDownIcon class="size-3"
                /></button>
              </div>
              <Transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="opacity-0 scale-95"
                enter-to-class="opacity-100 scale-100"
                leave-active-class="transition duration-75 ease-in"
                leave-from-class="opacity-100 scale-100"
                leave-to-class="opacity-0 scale-95">
                <div
                  v-if="previewMenuOpen"
                  data-test="preview-menu"
                  class="absolute right-0 bottom-full z-50 mb-2 w-80 max-w-[calc(100vw-16px)] origin-bottom-right rounded-xl bg-white py-1 text-copy shadow-popover ring-1 ring-line dark:bg-panel">
                  <div
                    class="px-2.5 py-1.5 text-[10px] font-semibold tracking-wider text-gray-400 uppercase dark:text-gray-500">
                    Preview on
                  </div>

                  <div
                    v-if="previewUrls.isLoading.value && previewUrls.hitlist.value.length === 0"
                    class="px-2.5 py-2 text-sm text-gray-500 dark:text-gray-400">
                    Loading pages…
                  </div>

                  <template v-for="entry in previewUrls.hitlist.value" :key="entry.url">
                    <button
                      type="button"
                      class="flex w-full cursor-pointer items-start gap-x-2.5 px-2.5 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-slate-700/50"
                      @click="openPreviewEntry(entry.url, entry.source)">
                      <GlobeAltIcon
                        class="mt-0.5 h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500"
                        aria-hidden="true" />
                      <div class="min-w-0 flex-1">
                        <div class="truncate font-medium">{{ getPreviewUrlLabel(entry.url) }}</div>
                        <div class="text-[11px] text-gray-400 dark:text-gray-500">
                          {{
                            entry.source === 'custom'
                              ? 'Custom URL'
                              : `${entry.uniqueVisitors} visitors · ${entry.events} events`
                          }}
                        </div>
                      </div>
                    </button>
                  </template>

                  <div
                    v-if="!previewUrls.isLoading.value && previewUrls.hitlist.value.length === 0"
                    class="px-2.5 py-2 text-sm text-gray-500 dark:text-gray-400">
                    No tracked pages yet
                  </div>

                  <div class="my-1 border-t border-gray-200 dark:border-slate-700" />

                  <button
                    data-test="preview-enter-url-button"
                    type="button"
                    class="flex w-full cursor-pointer items-center gap-x-2.5 px-2.5 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-slate-700/50"
                    @click="openCustomPreviewDialog">
                    <GlobeAltIcon class="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                    <div class="font-medium">Enter URL…</div>
                  </button>
                </div>
              </Transition>
            </div>
          </span>
          <button
            data-test="publish-button"
            type="button"
            :disabled="publishDisabled || isPublishingChanges"
            class="flex h-[34px] shrink-0 cursor-pointer items-center gap-1.5 rounded-lg bg-orange-600 px-4 font-semibold whitespace-nowrap text-[#fffaf3] hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-blue-600 dark:text-[#e0def4] dark:hover:bg-blue-700"
            @click="attemptToPublish">
            <ArrowPathIcon v-if="isPublishingChanges" class="size-4 animate-spin motion-reduce:animate-none" />{{
              isPublishingChanges ? 'Publishing…' : 'Publish'
            }}
          </button>
        </template>
        <PublishChangesPopover
          v-if="changesOpen && !isPublishingChanges"
          :id="changesPopoverId"
          :changes="configChanges"
          :changed-at="changeTimes.changedAt"
          :disabled="publishDisabled"
          @mouseenter="cancelChangesTimer"
          @mouseleave="scheduleChangesClose"
          @close="closeChangesPopover"
          @review="reviewChanges" />
      </FloatingActionBar>
    </TransitionGroup>
    <PublishErrors
      :visible="publishErrorsVisible"
      :validation-errors="validationErrors"
      @update:visible="publishErrorsVisible = $event" />
    <PublishChangesModal
      :visible="publishChangesModalVisible"
      :changes="configChanges"
      :is-publishing="isPublishingChanges"
      :published="publishSucceeded"
      :can-preview="!previewDisabled"
      :hosted-block="hostedBlock"
      :hosted-stale="hostedReviewStale"
      :hosted-result="hostedResult"
      @update:visible="handlePublishChangesVisibleChange"
      @preview="openActivePreview"
      @refresh-hosted="refreshHostedReview"
      @publish="handlePublishWithMessage" />
    <SnapshotCreationModal :open="openCreateSnapshotModal" @update-visible="openCreateSnapshotModal = $event" />
    <PublishSubscribeModal
      :key="String(currentTeamPid)"
      :visible="publishSubscribeModalVisible"
      :plans="subscriptionPlans"
      :setup-requirements="subscriptionRequirements"
      :project-start="subscriptionProjectStart"
      :publish-message="pendingPublishMessage"
      :activation-recovery="activationRecovery"
      @update:visible="handlePublishSubscribeVisibleChange"
      @subscribed="handleSubscribedAndPublished" />
    <Dialog
      v-model:visible="customPreviewDialogVisible"
      modal
      header="Enter preview URL"
      :style="{ width: '32rem', maxWidth: 'calc(100vw - 2rem)' }"
      @hide="resetCustomPreviewDialog">
      <form class="space-y-4" @submit.prevent="submitCustomPreviewUrl">
        <div class="space-y-2">
          <label for="publish-preview-url" class="block text-sm font-medium text-gray-900 dark:text-slate-100"
            >URL</label
          >
          <InputText
            id="publish-preview-url"
            v-model="customPreviewUrl"
            data-test="preview-custom-url-input"
            type="url"
            class="w-full"
            :invalid="!!customPreviewUrlError"
            autocomplete="url"
            aria-describedby="publish-preview-url-help publish-preview-url-error"
            @blur="handleCustomPreviewUrlBlur" />
          <p id="publish-preview-url-help" class="text-xs text-gray-500 dark:text-slate-400">
            Enter the full page URL you want to preview, including http:// or https://
          </p>
          <p
            v-if="customPreviewUrlError"
            id="publish-preview-url-error"
            data-test="preview-custom-url-error"
            class="text-sm text-red-600 dark:text-red-400">
            {{ customPreviewUrlError }}
          </p>
        </div>
        <div class="flex justify-end gap-2">
          <Button type="button" label="Cancel" outlined @click="closeCustomPreviewDialog" />
          <Button data-test="preview-custom-url-submit" type="submit" label="Open Preview" />
        </div>
      </form>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import {
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CreditCardIcon,
  ExclamationCircleIcon,
  EyeIcon,
  GlobeAltIcon,
  XMarkIcon
} from '@heroicons/vue/24/outline'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import PublishChangesPopover from './PublishChangesPopover.vue'
import FloatingActionBar from './FloatingActionBar.vue'
import { useBottomStackOverlay, stackListTransition } from '@dashboard/composables/useBottomStack'
import { usePreviewUrls } from '@dashboard/composables/usePreviewUrls'
import { normalizePreviewUrl } from '@dashboard/utils/previewUrl'
import { useConfigChangeTimes } from '@dashboard/composables/useConfigChangeTimes'
import { usePublishReviewRequest } from '@dashboard/composables/usePublishReviewRequest'
import { useReportStore } from '@dashboard/modules/dashboard/stores/reportStore'
import axios from 'axios'
import api from '@dashboard/services/axiosApi'
import { recordAnalyticsEvent, AnalyticsEventTypes } from '@dashboard/services/analytics'
import type { RegistrationPlanApiResponse } from '@dashboard/modules/user/services/registrationPlans'
import { useConfigChanges } from '@dashboard/composables/useConfigChanges'
import type { ConfigChange } from '@dashboard/composables/useConfigChanges'
import { pageOwnedWidgetChanges } from '@dashboard/services/publishFieldVocabulary'
import { projectStartSchema, setupRequirementsSchema } from '@dashboard/types/user'
import type { ProjectStart, SetupRequirements, TrialActivation } from '@dashboard/types/user'
import { refreshBillingOwner } from '@dashboard/components/billing/refreshBillingOwner'

import SnapshotCreationModal from '@dashboard/components/common/SnapshotCreationModal.vue'
import PublishChangesModal from '@dashboard/components/common/PublishChangesModal.vue'
import PublishSubscribeModal from '@dashboard/components/common/PublishSubscribeModal.vue'
import { useIsFetching, useQuery, useQueryClient } from '@tanstack/vue-query'
import { watchDebounced, onClickOutside, onKeyStroke } from '@vueuse/core'
import PublishErrors from '@dashboard/components/common/PublishErrors.vue'
import { useBillingUrl } from '@dashboard/composables/useBillingUrl'
import { useAuthStore } from '@dashboard/stores/authStore'
import { useBillingDialogStore } from '@dashboard/stores/billingDialogStore'
import { isPublishStillRunningError, useConfigStore } from '@dashboard/stores/configStore'
import { useNotificationStore } from '@dashboard/stores/notificationStore'
import { usePermissionStore } from '@dashboard/stores/permissionStore'
import { useSubscriptionStore } from '@dashboard/stores/subscriptionStore'
import { storeToRefs } from 'pinia'
import { computed, inject, ref, watch, onBeforeUnmount, useId, unref } from 'vue'
import type { PublishContext } from '@shared/configChanges'
import { unstartedSplitTestReminders, type UnstartedSplitTest } from '@shared/splitTests/unstartedReminder'
import { RouterLink, useRoute } from 'vue-router'
import { ROUTES } from '@dashboard/router/routeNames'
import { useHostedDraftRefresh, useHostedSite } from '@dashboard/modules/pages/composables/useHostedSite'
import type { HostedPublishResult } from '@dashboard/modules/pages/services/hostedApi'

const authStore = useAuthStore()
const { isAdmin } = storeToRefs(authStore)

const configStore = useConfigStore()
const notificationStore = useNotificationStore()
const { publish } = configStore
const { currentTeam, currentTeamPid, config, fullResponse } = storeToRefs(configStore)
const { isUpdatingConfig } = storeToRefs(configStore)

const subscriptionStore = useSubscriptionStore()
const {
  isActive,
  isPaymentFailed,
  hasNoSubscription,
  isTrialExpired,
  trialActivation,
  isFreeAccount,
  isAdmin: isOwnerAdmin
} = storeToRefs(subscriptionStore)
const { canAccess } = usePermissionStore()
const billingDialog = useBillingDialogStore()

const injectedDemo = inject('isDemo', false)
const isDemo = computed(() => unref(injectedDemo))

const { goToBillingPortal } = useBillingUrl()

const { analyzeChanges } = useConfigChanges()
const queryClient = useQueryClient()
const route = useRoute()
const splitTestsQuery = useQuery({
  queryKey: ['splitTests', currentTeamPid],
  queryFn: () =>
    api
      .get<(PublishContext['splitTests'][number] & { reminder_dismissed?: boolean })[]>(
        `/api/${currentTeamPid.value}/splitTests`
      )
      .then((response) => response.data),
  enabled: computed(() => !!currentTeamPid.value)
})
// The demo serves no hosted routes, and Vue Query retries a 404 for about 7 seconds.
const hostedSiteEnabled = computed(() => !isDemo.value)
const hostedSite = useHostedSite(currentTeamPid, hostedSiteEnabled)
useHostedDraftRefresh(computed(() => (hostedSiteEnabled.value ? (currentTeamPid.value ?? null) : null)))
// `refetch()` ignores `enabled`, so every explicit refresh goes through this check.
const refreshHostedSite = async () => {
  if (hostedSiteEnabled.value) {
    await hostedSite.refetch()
  }
}
const hostedBlock = computed(() => hostedSite.data.value?.block ?? null)
// Set when Publish answered that pages changed after the review opened; the review asks for a refresh.
const hostedReviewStale = ref(false)
const hostedResult = ref<HostedPublishResult | null>(null)
const editorFlowId = computed(() => {
  // This bar also mounts on pages that have no router record. No route means no open flow.
  const name = route?.name
  if (name !== ROUTES.OFFERFUNNEL && name !== ROUTES.OFFERFUNNELNODE) {
    return null
  }
  const flowId = route?.params?.offerFunnelId
  return typeof flowId === 'string' ? flowId : null
})
const draftSplitTests = computed(() =>
  unstartedSplitTestReminders(
    (splitTestsQuery.data.value ?? []) as UnstartedSplitTest[],
    config.value,
    editorFlowId.value
  )
)

async function dismissSplitTestReminder(pid: string) {
  const queryKey = ['splitTests', currentTeamPid.value] as const
  const previous = splitTestsQuery.data.value
  queryClient.setQueryData(queryKey, (current: typeof previous) =>
    current?.map((test) => (test.pid === pid ? { ...test, reminder_dismissed: true } : test))
  )
  try {
    await api.post('/api/user/dismissals', { key: `split-test:${pid}` }, { skipProgress: true })
  } catch {
    queryClient.setQueryData(queryKey, previous)
    notificationStore.addError('Could not dismiss this reminder. Please try again.')
  }
}

const validationErrors = ref<{ message: string; references: string[] }[]>([])
const openCreateSnapshotModal = ref(false)

const publishErrorsVisible = ref(false)
const publishChangesModalVisible = ref(false)
// The dialog stays open on its success state until the user closes it.
const publishSucceeded = ref(false)
const handlePublishChangesVisibleChange = (visible: boolean) => {
  publishChangesModalVisible.value = visible
  if (!visible) {
    publishSucceeded.value = false
  }
}
const configChanges = ref<ConfigChange[]>([])
const isPublishingChanges = ref(false)
const publishSubscribeModalVisible = ref(false)
const subscriptionPlans = ref<RegistrationPlanApiResponse[]>([])
// undefined: the plans came without setup requirements (a 402 publish fallback).
const subscriptionRequirements = ref<SetupRequirements | null | undefined>(undefined)
const subscriptionProjectStart = ref<ProjectStart | null | undefined>(undefined)
const pendingPublishMessage = ref<string | null>(null)
const activationRecovery = ref<TrialActivation | null>(null)

const publishBarShown = computed(() => configChanges.value.length > 0 || isPublishingChanges.value)
const changesOpen = ref(false)
const changesPopoverId = useId()
const changesTriggerRef = ref<HTMLButtonElement | null>(null)
const barRef = ref<InstanceType<typeof FloatingActionBar> | null>(null)
const changeTimes = useConfigChangeTimes()
let analysisGeneration = 0
let changesTimer: ReturnType<typeof setTimeout> | undefined
function cancelChangesTimer() {
  clearTimeout(changesTimer)
}
function closeChangesPopover() {
  cancelChangesTimer()
  changesOpen.value = false
}
function scheduleChangesOpen() {
  cancelChangesTimer()
  if (!isPublishingChanges.value) {
    changesTimer = setTimeout(() => {
      changesOpen.value = true
    }, 150)
  }
}
function scheduleChangesClose() {
  cancelChangesTimer()
  changesTimer = setTimeout(() => {
    changesOpen.value = false
  }, 150)
}
function toggleChangesPopover() {
  cancelChangesTimer()
  changesOpen.value = !changesOpen.value
  closePreviewMenu()
}
function reviewChanges() {
  closeChangesPopover()
  void attemptToPublish()
}
onClickOutside(barRef, closeChangesPopover)
onKeyStroke('Escape', () => {
  if (changesOpen.value) {
    closeChangesPopover()
    changesTriggerRef.value?.focus()
  }
})
onBeforeUnmount(cancelChangesTimer)
watch(publishBarShown, (shown) => {
  if (!shown) {
    closeChangesPopover()
    closePreviewMenu()
  }
})
const isDemoMode = isDemo
const previewUrls = usePreviewUrls({ teamPid: currentTeamPid, isDemoMode })
const { dataLastReceived, isLoadingDataLastReceived, isErrorDataLastReceived } = storeToRefs(useReportStore())

// Preview loads the user's website — pointless until the snippet is installed
// and has reported at least one event
const previewDisabled = computed(() => {
  if (isDemoMode.value) {
    return false
  }
  if (isLoadingDataLastReceived.value) {
    return false
  }
  // A reporting query error is not evidence the snippet is missing — keep preview usable
  if (isErrorDataLastReceived.value) {
    return false
  }
  return !dataLastReceived.value?.last_update
})

const previewMenuOpen = ref(false)
const previewMenuRef = ref<HTMLElement | null>(null)
const customPreviewDialogVisible = ref(false)
const customPreviewUrl = ref('')
const customPreviewUrlError = ref('')

function closePreviewMenu() {
  if (!previewMenuOpen.value) {
    return
  }

  previewMenuOpen.value = false
}

onClickOutside(previewMenuRef, closePreviewMenu)
onKeyStroke('Escape', closePreviewMenu)
useBottomStackOverlay(() => changesOpen.value || previewMenuOpen.value)

watch([currentTeamPid, previewDisabled], closePreviewMenu)
const getPreviewUrlLabel = (url: string) => {
  try {
    const parsedUrl = new URL(url)
    return `${parsedUrl.hostname}${parsedUrl.pathname}`
  } catch {
    return url
  }
}

const customPreviewDefaultUrl = computed(() => {
  const website = currentTeam.value?.website?.trim()
  if (!website) {
    return ''
  }

  const normalizedUrl = normalizePreviewUrl(website)
  if (!normalizedUrl) {
    return ''
  }

  return new URL(normalizedUrl).origin
})

function resetCustomPreviewDialog() {
  customPreviewUrl.value = ''
  customPreviewUrlError.value = ''
}

function closeCustomPreviewDialog() {
  customPreviewDialogVisible.value = false
  resetCustomPreviewDialog()
}
function validateCustomPreviewUrl(value: string) {
  const trimmedValue = value.trim()

  if (!/^https?:\/\//i.test(trimmedValue)) {
    customPreviewUrlError.value = 'Enter a full URL starting with http:// or https://.'
    return null
  }

  const normalizedUrl = normalizePreviewUrl(trimmedValue)
  if (!normalizedUrl || !new URL(normalizedUrl).hostname) {
    customPreviewUrlError.value = 'Enter a valid page URL.'
    return null
  }

  customPreviewUrlError.value = ''
  return normalizedUrl
}

function handleCustomPreviewUrlBlur() {
  validateCustomPreviewUrl(customPreviewUrl.value)
}

watch(customPreviewUrl, (value) => {
  if (customPreviewUrlError.value) {
    validateCustomPreviewUrl(value)
  }
})

function openCustomPreviewDialog() {
  closePreviewMenu()
  customPreviewUrl.value = customPreviewDefaultUrl.value
  customPreviewUrlError.value = ''
  customPreviewDialogVisible.value = true
}

async function submitCustomPreviewUrl() {
  const normalizedUrl = validateCustomPreviewUrl(customPreviewUrl.value)
  if (!normalizedUrl) {
    return
  }

  try {
    await previewUrls.openPreviewUrl(normalizedUrl, 'custom')
    closeCustomPreviewDialog()
  } catch {
    notificationStore.addError('Could not open the preview. Please try again.')
  }
}

const openActivePreview = async () => {
  closePreviewMenu()
  if (!previewUrls.activeUrl.value) {
    openCustomPreviewDialog()
    return
  }

  try {
    await previewUrls.openActivePreview()
  } catch {
    notificationStore.addError('Could not open the preview. Please try again.')
  }
}

const openPreviewEntry = async (url: string, source: 'suggested' | 'custom') => {
  closePreviewMenu()
  try {
    await previewUrls.openPreviewUrl(url, source)
  } catch {
    notificationStore.addError('Could not open the preview. Please try again.')
  }
}

const togglePreviewMenu = () => {
  if (previewDisabled.value) {
    return
  }

  closeChangesPopover()
  previewMenuOpen.value = !previewMenuOpen.value
  if (previewMenuOpen.value) {
    void previewUrls.loadSuggestions()
  }
}
const resetPendingPublishState = () => {
  pendingPublishMessage.value = null
  subscriptionPlans.value = []
  subscriptionRequirements.value = undefined
  subscriptionProjectStart.value = undefined
  activationRecovery.value = null
}

// Analyze changes between draft and published config
const analyzeConfigChanges = async () => {
  if (!config.value || !fullResponse.value) {
    configChanges.value = []
    return
  }

  const teamPid = currentTeamPid.value
  const generation = ++analysisGeneration
  const publishedConfig = fullResponse.value.stable || null
  const site = hostedSite.data.value
  const analyzed = await analyzeChanges(
    config.value,
    publishedConfig,
    splitTestsQuery.data.value ?? [],
    site ? { changes: site.changes, servingHost: site.servingHost } : null
  )
  if (currentTeamPid.value !== teamPid || generation !== analysisGeneration) {
    return
  }
  const changes = pageOwnedWidgetChanges(
    analyzed,
    config.value,
    publishedConfig,
    new Map((site?.pages ?? []).map((page) => [page.id, page.name]))
  )
  configChanges.value = changes
  if (teamPid !== null) {
    changeTimes.recordChanges(String(teamPid), changes)
  }
}

const loadSubscriptionPlans = async (teamPid = currentTeamPid.value) => {
  const { data } = await api.get(`/api/${teamPid}/billing/availablePlans`)
  if (currentTeamPid.value !== teamPid) {
    return
  }
  subscriptionPlans.value = data.plans || []
  subscriptionRequirements.value = data.setup_requirements ?? null
  subscriptionProjectStart.value = data.project_start ?? null
  activationRecovery.value = data.trial_activation ?? null
  if (!activationRecovery.value) {
    await refreshBillingOwner(teamPid)
  }
}

const canPublishAfterRecovery = computed(
  () => !activationRecovery.value && !trialActivation.value && isActive.value && !hasNoSubscription.value
)

watch(currentTeamPid, () => {
  analysisGeneration++
  configChanges.value = []
  closeChangesPopover()
  publishSubscribeModalVisible.value = false
  publishChangesModalVisible.value = false
  hostedReviewStale.value = false
  hostedResult.value = null
  resetPendingPublishState()
})

// Watch for config changes with debouncing to avoid excessive analysis during rapid edits
watchDebounced(
  [config, fullResponse, splitTestsQuery.data, hostedSite.data],
  () => {
    analyzeConfigChanges()
  },
  { debounce: 1000, deep: true, immediate: true }
)

// The selected team's config query (`['config', currentTeamPid]`) and an
// in-flight config save block publish. Unrelated Vue Query traffic, including
// campaign results analytics, must not.
const isFetchingConfig = useIsFetching({
  queryKey: ['config', currentTeamPid]
})

const publishDisabled = computed(
  () =>
    !isAdmin.value &&
    (isUpdatingConfig.value || isFetchingConfig.value > 0 || !currentTeam.value || configChanges.value.length === 0)
)

// Every pending change is a hosted one, so publishing could ship nothing while the gate fails.
const onlyHostedChanges = computed(
  () => configChanges.value.length > 0 && configChanges.value.every((change) => change.hosted)
)

const attemptToPublish = async () => {
  if (isDemo.value) {
    alert(`Drats! Publishing isn't available within the demo sandbox.`)
    return
  }
  const teamPid = currentTeamPid.value
  // The review's digest must name the hosted changes it shows.
  await refreshHostedSite()
  await analyzeConfigChanges()
  if (currentTeamPid.value !== teamPid) {
    return
  }
  // Config rules guard config edits; a publish of page changes alone re-sends the config as it is.
  const errors = onlyHostedChanges.value ? [] : configStore.validateForPublish()

  if (errors.length > 0) {
    validationErrors.value = errors
    publishErrorsVisible.value = true
  } else {
    hostedReviewStale.value = false
    hostedResult.value = null
    publishSucceeded.value = false
    publishChangesModalVisible.value = true
  }
}

const refreshHostedReview = async () => {
  const teamPid = currentTeamPid.value
  await refreshHostedSite()
  await analyzeConfigChanges()
  if (currentTeamPid.value === teamPid) {
    hostedReviewStale.value = false
  }
}

const isHostedDigestConflict = (error: unknown) =>
  axios.isAxiosError(error) &&
  error.response?.status === 409 &&
  typeof error.response.data === 'object' &&
  error.response.data !== null &&
  'code' in error.response.data &&
  error.response.data.code === 'hosted_digest_mismatch'

const publishWithHostedDigest = async (message: string, options: { notify?: boolean } = {}) => {
  const teamPid = currentTeamPid.value
  const pageNames = new Map((hostedSite.data.value?.pages ?? []).map((page) => [page.id, page.name]))
  const response = await publish(message, { ...options, hostedDigest: hostedSite.data.value?.digest ?? null })
  if (currentTeamPid.value !== teamPid) {
    return
  }
  void refreshHostedSite()
  const hosted = response?.hosted
  if (!hosted) {
    return
  }
  hostedResult.value = {
    ...hosted,
    blockedPages: hosted.blockedPages.map((page) => ({ ...page, name: pageNames.get(page.pageId) ?? 'A page' }))
  }
  if (hosted.outcome === 'blocked') {
    notificationStore.addError(`Your pages weren't published. ${hosted.reason ?? ''}`.trim())
  } else if (hosted.outcome === 'failed') {
    notificationStore.addError(
      `Your pages didn't publish${hosted.reason ? `: ${hosted.reason}` : ''}. Publish again to retry.`
    )
  }
}

// README 3.1: the card prompt opens after the first successful publish of an
// un-activated no-card trial, for viewers who can set up billing.
const firstPublishCardPromptEligible = () => {
  const trial = trialActivation.value
  return (
    !isDemo.value &&
    !isAdmin.value &&
    !isOwnerAdmin.value &&
    !isFreeAccount.value &&
    !currentTeam.value?.first_published_at &&
    trial !== null &&
    !trial.pending_activation &&
    !isTrialExpired.value &&
    canAccess('manage-billing') === true
  )
}

const handlePublishWithMessage = async (message: string) => {
  const teamPid = currentTeamPid.value
  const promptForCard = firstPublishCardPromptEligible()
  isPublishingChanges.value = true
  try {
    if (
      hasNoSubscription.value ||
      trialActivation.value?.pending_activation ||
      (trialActivation.value && isTrialExpired.value)
    ) {
      pendingPublishMessage.value = message

      try {
        activationRecovery.value = trialActivation.value
        await loadSubscriptionPlans(teamPid)
        if (currentTeamPid.value !== teamPid) {
          return
        }
        if (!canPublishAfterRecovery.value) {
          publishChangesModalVisible.value = false
          publishSubscribeModalVisible.value = true
          recordAnalyticsEvent(AnalyticsEventTypes.PAYWALL_HIT)
          return
        }
        resetPendingPublishState()
      } catch {
        if (currentTeamPid.value !== teamPid) {
          return
        }
        publishChangesModalVisible.value = true
        pendingPublishMessage.value = null
        notificationStore.addError('Could not load billing plans. Please try again before publishing.')
        return
      }
    }

    await publishWithHostedDigest(message, { notify: !promptForCard })
    publishSucceeded.value = true
    if (promptForCard && currentTeamPid.value === teamPid) {
      handlePublishChangesVisibleChange(false)
      billingDialog.open({ mode: 'post-publish', source: 'post-publish' })
    } else if (promptForCard) {
      notificationStore.addNotification('Successfully published your changes!')
    }
  } catch (error: unknown) {
    if (currentTeamPid.value !== teamPid) {
      return
    }
    if (
      axios.isAxiosError(error) &&
      error.response?.status === 402 &&
      error.response?.data &&
      typeof error.response.data === 'object' &&
      'requires_subscription' in error.response.data &&
      error.response.data.requires_subscription
    ) {
      pendingPublishMessage.value = message
      const plans = error.response.data.plans
      subscriptionPlans.value = Array.isArray(plans) ? plans : []
      const requirements = setupRequirementsSchema.safeParse(
        'setup_requirements' in error.response.data ? error.response.data.setup_requirements : undefined
      )
      subscriptionRequirements.value = requirements.success ? requirements.data : undefined
      const projectStart = projectStartSchema.safeParse(
        'project_start' in error.response.data ? error.response.data.project_start : undefined
      )
      subscriptionProjectStart.value = projectStart.success ? projectStart.data : undefined
      publishChangesModalVisible.value = false
      publishSubscribeModalVisible.value = true
      recordAnalyticsEvent(AnalyticsEventTypes.PAYWALL_HIT)
    } else if (isHostedDigestConflict(error)) {
      hostedReviewStale.value = true
    } else if (isPublishStillRunningError(error)) {
      // The server is still publishing; the store's notice reports it, and a second Publish here would only queue behind it.
      handlePublishChangesVisibleChange(false)
    } else {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 409 &&
        error.response?.data &&
        typeof error.response.data === 'object' &&
        'code' in error.response.data &&
        error.response.data.code === 'billing_state_conflict'
      ) {
        publishChangesModalVisible.value = false
        if (error.response.data.trial_activation) {
          activationRecovery.value = error.response.data.trial_activation
          pendingPublishMessage.value = message
          try {
            await loadSubscriptionPlans(teamPid)
            if (currentTeamPid.value !== teamPid) {
              return
            }
            if (activationRecovery.value) {
              await refreshBillingOwner(teamPid)
              if (currentTeamPid.value !== teamPid) {
                return
              }
            }
            if (canPublishAfterRecovery.value) {
              resetPendingPublishState()
              await publishWithHostedDigest(message)
            } else {
              publishSubscribeModalVisible.value = true
            }
          } catch {
            notificationStore.addError('Could not load billing recovery. Please retry publishing to resume activation.')
          }
          return
        }
      }
    }
    // Other errors are handled by the configStore publish mutation's onError
  } finally {
    isPublishingChanges.value = false
  }
}

const handlePublishSubscribeVisibleChange = (visible: boolean) => {
  publishSubscribeModalVisible.value = visible
  if (!visible) {
    resetPendingPublishState()
  }
}

const handleSubscribedAndPublished = () => {
  resetPendingPublishState()
  publishSubscribeModalVisible.value = false
  publishChangesModalVisible.value = false
  void refreshHostedSite()
}

const { requestCount } = usePublishReviewRequest()
watch(requestCount, () => {
  void attemptToPublish()
})
watch(isPublishingChanges, (publishing) => {
  if (publishing) {
    closeChangesPopover()
    closePreviewMenu()
  }
})
</script>
